import { FC, ReactElement, useState } from 'react';

import { RaaExpandablePreview } from 'raa-react';

import { DrawerSection } from 'components/drawerSections/DrawerSections';
import SkeletonImage from 'components/skeleton/SkeletonImage';

import { DokumentationstypEnum } from 'enums';

import DokumentationPresentation from './DokumentationPresentation';

import classNames from 'classnames';

const BebyggelseDokumentationSection: FC<{
  uri: string;
  utkastId?: string;
  totalDocuments: number;
  includeRitningar: boolean;
}> = ({ uri, utkastId, totalDocuments, includeRitningar }): ReactElement => {
  const [dokumentationShowing, setDokumentationShowing] = useState(false);
  const [loadingFotografi, setLoadingFotografi] = useState(false);
  const [loadingRitning, setLoadingRitning] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  const handleRitningLoadingStateChange = (isLoading: boolean) => setLoadingRitning(isLoading);
  const handleFotografiLoadingStateChange = (isLoading: boolean) => setLoadingFotografi(isLoading);

  return (
    <DrawerSection>
      <h2 className="tw-heading-md tw-mb-4">Dokumentation{totalDocuments > 0 && ` (${totalDocuments} st)`}</h2>
      {totalDocuments === 0 ? (
        <p>Ingen dokumentation</p>
      ) : (
        <RaaExpandablePreview
          ariaLabel="Visa dokumentation"
          height={30}
          onShow={() => {
            if (!hasInitialized) {
              setHasInitialized(true);
            }

            setDokumentationShowing(true);
          }}
          onHide={() => setDokumentationShowing(false)}
        >
          <>
            {!dokumentationShowing && (
              <ul className="tw-grid tw-grid-cols-2 tw-gap-4 md:tw-grid-cols-3 lg:tw-grid-cols-4">
                {Array.from({ length: totalDocuments > 4 ? 4 : totalDocuments }, (_, i) => (
                  <li key={i}>
                    <SkeletonImage
                      key={i}
                      skeletonClassNames="tw-h-20 tw-w-full"
                      animated={false}
                      alt="Laddningsbild"
                    />
                  </li>
                ))}
              </ul>
            )}

            {(loadingRitning || loadingFotografi) && (
              <ul className="tw-grid tw-grid-cols-2 tw-gap-4 md:tw-grid-cols-3 lg:tw-grid-cols-4">
                {Array.from({ length: totalDocuments > 4 ? 4 : totalDocuments }, (_, i) => (
                  <li key={i}>
                    <SkeletonImage
                      key={i}
                      skeletonClassNames="tw-h-20 tw-w-full"
                      animated={true}
                      alt="Uppladdat fotografi"
                    />
                  </li>
                ))}
              </ul>
            )}

            <div
              className={classNames({
                'tw-hidden': loadingRitning || loadingFotografi || !dokumentationShowing || !hasInitialized
              })}
            >
              {hasInitialized && (
                <>
                  {includeRitningar && (
                    <DokumentationPresentation
                      uri={uri}
                      bebyggelseobjektId={utkastId}
                      dokumentationstyp={DokumentationstypEnum.RITNING}
                      onLoadingStateChange={handleRitningLoadingStateChange}
                    />
                  )}

                  <DokumentationPresentation
                    uri={uri}
                    bebyggelseobjektId={utkastId}
                    dokumentationstyp={DokumentationstypEnum.BILD}
                    onLoadingStateChange={handleFotografiLoadingStateChange}
                  />
                </>
              )}
            </div>
          </>
        </RaaExpandablePreview>
      )}
    </DrawerSection>
  );
};

export default BebyggelseDokumentationSection;
